import produce from "immer";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchReports,
  fetchReport,
  updateReport,
  createReport,
  deleteReport,
  listUpdateReport,
  addReportItem,
} from "../../api/service/report";
import { normalizeError, hasMeaningfulErrors } from "../../operationFeedback";

const initialState = {
  isLoading: false,
  error: false,
  errors: null,
  data: [],
  report: {},
  count: 0,
  currentCount: 0,
  newestReportsCount: 0,
};

export const initialController = {
  filters: [],
  page: 0,
  query: "",
  sort: "desc",
  sortBy: "appointment",
  view: "all",
};

function rejectNormalized(rejectWithValue, err, operationKey) {
  return rejectWithValue(normalizeError(err, { operationKey }));
}

function isHttpFailureStatus(status) {
  const n = Number(status);
  return Number.isFinite(n) && n >= 400;
}

const GENERIC_SAFE =
  /^(En uventet feil oppstod\.|Handlingen kunne ikke fullføres\.)$/;

function rejectedErrors(action, fallbackNb) {
  const msg = action.payload?.safeMessage;
  if (typeof msg === "string" && msg.trim() && !GENERIC_SAFE.test(msg)) {
    return [msg];
  }
  return [fallbackNb];
}

export const deleteReportAsync = createAsyncThunk(
  "reports/deleteReportAsync",
  async (payload, { rejectWithValue }) => {
    try {
      return await deleteReport(payload);
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "delete_report");
    }
  }
);

export const createReportAsync = createAsyncThunk(
  "reports/createReportAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await createReport(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "create_report");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "create_report");
    }
  }
);

export const updateReportAsync = createAsyncThunk(
  "reports/updateReportAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateReport(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "update_report");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "update_report");
    }
  }
);

export const addReportItemAsync = createAsyncThunk(
  "reports/addReportItemAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await addReportItem(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "add_report_item");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "add_report_item");
    }
  }
);

export const getReportsAsync = createAsyncThunk(
  "reports/getReportsAsync",
  async (controller = initialController, { rejectWithValue }) => {
    try {
      const response = await fetchReports(controller);
      const status = response?.response?.status ?? response?.status;

      if (
        isHttpFailureStatus(status) ||
        hasMeaningfulErrors(response?.errors)
      ) {
        return rejectNormalized(rejectWithValue, response, "list_reports");
      }

      if (!Array.isArray(response?.data)) {
        return rejectNormalized(rejectWithValue, response, "list_reports");
      }

      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "list_reports");
    }
  }
);

export const getReportAsync = createAsyncThunk(
  "reports/getReportAsync",
  async (id, { rejectWithValue }) => {
    try {
      const response = await fetchReport(id);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "get_report");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "get_report");
    }
  }
);

export const listUpdateReportAsync = createAsyncThunk(
  "reports/listUpdateReportAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await listUpdateReport(payload);
      const status = response?.response?.status ?? response?.status;
      if (
        isHttpFailureStatus(status) ||
        hasMeaningfulErrors(response?.errors)
      ) {
        return rejectNormalized(
          rejectWithValue,
          response,
          "list_update_reports"
        );
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "list_update_reports");
    }
  }
);

const reportSlice = createSlice({
  name: "reports",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getReportsAsync.pending, (state, action) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
        // Clear data immediately on non-append fetches so the skeleton shows
        if (!action.meta?.arg?.append) {
          state.data = [];
          state.count = 0;
        }
      })
      .addCase(getReportsAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        const rows = action.payload?.data ?? [];
        if (action.meta?.arg?.append) {
          // Infinite scroll: concatenate new page to existing rows
          state.data = [...(state.data || []), ...rows];
        } else {
          state.data = rows;
        }
        state.count = action.payload?.count ?? 0;
        state.currentCount = state.data.length;
        state.newestReportsCount = action.payload?.newestReportsCount ?? 0;
      })
      .addCase(getReportsAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke hente rapportene.");
      })
      .addCase(updateReportAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(updateReportAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          const id = action.payload?.id;
          const index = draftState.data.findIndex((type) => type.id === id);
          if (index >= 0) {
            draftState.data[index] = action.payload;
          }
          draftState.isLoading = false;
        });
      })
      .addCase(updateReportAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke oppdatere rapporten.");
      })
      .addCase(createReportAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(createReportAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.data.push(action.payload);
          draftState.isLoading = false;
        });
      })
      .addCase(createReportAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke opprette rapporten.");
      })
      .addCase(deleteReportAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(deleteReportAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.data = action.payload;
          draftState.isLoading = false;
        });
      })
      .addCase(deleteReportAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke slette rapporten.");
      })
      .addCase(getReportAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(getReportAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.report = action.payload;
          draftState.isLoading = false;
        });
      })
      .addCase(getReportAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke hente rapporten.");
      })
      .addCase(listUpdateReportAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(listUpdateReportAsync.fulfilled, (state, action) => {
        state.report = action.payload;
        state.isLoading = false;
      })
      .addCase(listUpdateReportAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(
          action,
          "Kunne ikke oppdatere rapportene."
        );
      });
  },
});

export const { reducer } = reportSlice;

export default reportSlice;
